import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Sparkles, CheckCircle2, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import HealthScore from "../components/HealthScore";
import AntiRecoCard from "../components/AntiRecoCard";
import ConfidenceBadge from "../components/ConfidenceBadge";
import { toast } from "sonner";

// Pick the atelier title in the requested locale, with FR fallback.
function pickTitle(a, locale) {
  if (!a) return "";
  if (locale === "en") return a.title_en || a.title_fr || a.id;
  return a.title_fr || a.title_en || a.id;
}

// Pick a signal's human label in the diagnostic's own locale (NOT the UI's).
// `groupe_locale` is set by the backend (`payload.groupe_locale`).
function signalLabel(sig, groupeLocale) {
  if (!sig) return "";
  if (groupeLocale === "en") return sig.label_en || sig.label_fr || sig.code;
  return sig.label_fr || sig.label_en || sig.code;
}

export default function DiagnosticPage() {
  const { groupeId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Stages are UI-language driven (user reads them in their own language).
  // The backend drives the ORDER via SSE `stage` events. We just render the label.
  const STAGES = [
    { key: "fetch", label: t("diag.stage_fetch", "Lecture du groupe…") },
    { key: "members", label: t("diag.stage_members", "Analyse des membres…") },
    { key: "history", label: t("diag.stage_history", "Lecture de l'historique…") },
    { key: "ateliers", label: t("diag.stage_ateliers", "Chargement du catalogue…") },
    { key: "ai", label: t("diag.stage_ai", "L'oracle réfléchit…") },
  ];

  const [phase, setPhase] = useState("ready"); // ready, running, done, error
  const [activeStage, setActiveStage] = useState(null);
  const [result, setResult] = useState(null);
  const [typedNote, setTypedNote] = useState("");
  const [stagesCompleted, setStagesCompleted] = useState([]);
  const [error, setError] = useState(null);

  const runDiagnostic = async () => {
    setPhase("running");
    setActiveStage(null);
    setStagesCompleted([]);
    setTypedNote("");
    setResult(null);
    setError(null);

    const token = localStorage.getItem("delphi_token");
    const impersonateId = localStorage.getItem("delphi_impersonate_id");
    const headers = {
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    };
    if (impersonateId) headers["X-Impersonate-User-Id"] = impersonateId;

    let response;
    try {
      response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/groupes/${groupeId}/diagnostic/stream`,
        { method: "POST", headers },
      );
    } catch (e) {
      setError(t("diag.stream_network_error", "Connexion à l'oracle impossible."));
      setPhase("error");
      return;
    }
    if (!response.ok || !response.body) {
      let detail = "";
      try { detail = (await response.json())?.detail; } catch (_) { /* noop */ }
      setError(detail || t("diag.stream_http_error", "Erreur lors du diagnostic"));
      setPhase("error");
      toast.error(detail || "");
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let finalPayload = null;

    while (true) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are delimited by a blank line
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const rawFrame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let event = "message";
        let dataLine = "";
        rawFrame.split("\n").forEach((line) => {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
        });
        if (!dataLine) continue;
        let data;
        try { data = JSON.parse(dataLine); } catch (_) { continue; }

        if (event === "stage") {
          if (data.status === "active") {
            setActiveStage(data.key);
          } else if (data.status === "done") {
            setStagesCompleted((prev) => (prev.includes(data.key) ? prev : [...prev, data.key]));
          }
        } else if (event === "text") {
          setTypedNote(data.typed || "");
        } else if (event === "done") {
          finalPayload = data;
        } else if (event === "error") {
          setError(data.detail || t("diag.stream_http_error", "Erreur lors du diagnostic"));
          setPhase("error");
          toast.error(data.detail || "");
          return;
        }
      }
    }

    if (finalPayload) {
      setResult(finalPayload);
      setPhase("done");
    } else {
      setError(t("diag.stream_incomplete", "Flux interrompu avant la fin : réessayez."));
      setPhase("error");
    }
  };

  if (phase === "ready") {
    return <ReadyView onStart={runDiagnostic} />;
  }

  if (phase === "error") {
    return (
      <div className="max-w-2xl mx-auto text-center py-20" data-testid="diagnostic-error">
        <div className="text-danger text-lg font-medium mb-4">{error}</div>
        <Button onClick={() => setPhase("ready")} variant="outline">{t("diag.regenerate")}</Button>
      </div>
    );
  }

  const groupeLocale = result?.ai_payload?.groupe_locale || "fr";

  return (
    <div className="max-w-4xl mx-auto" data-testid="diagnostic-page">
      <Link to={`/groupe/${groupeId}`} className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-apollo-orange-dark mb-6" data-testid="back-to-group">
        <ArrowLeft className="w-4 h-4" /> {t("nav.back")}
      </Link>

      {phase === "running" && (
        <div className="bg-white border border-border rounded-2xl p-8 mb-8" data-testid="diagnostic-stages">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-apollo-orange/10 mb-4 relative">
              <Sparkles className="w-8 h-8 text-apollo-orange animate-pulse" />
            </div>
            <h2 className="font-heading font-black text-2xl tracking-tighter">{t("diag.running")}</h2>
            <p className="font-oracle italic text-text-secondary text-lg mt-2">
              « {t("diag.oracle_quote", "Patience est la première vertu de qui consulte les dieux.")} »
            </p>
          </div>
          <div className="space-y-2 max-w-md mx-auto">
            {STAGES.map((s, i) => {
              const done = stagesCompleted.includes(s.key);
              const active = activeStage === s.key && !done;
              return (
                <div key={s.key} className={`flex items-center gap-3 px-4 py-2 rounded-lg ${active ? "bg-apollo-orange-light" : done ? "bg-success/5" : "bg-bg-soft"}`} data-testid={`stage-${s.key}`}>
                  {done ? (
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                  ) : active ? (
                    <Loader2 className="w-4 h-4 text-apollo-orange animate-spin flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-border flex-shrink-0" />
                  )}
                  <span className={`text-sm ${done ? "text-text-primary" : active ? "text-apollo-orange-dark font-medium" : "text-text-secondary"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>

          {typedNote && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 pt-6 border-t border-border"
              data-testid="streaming-note"
            >
              <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">
                {t("diag.oracle_note")}
              </div>
              <p className="font-oracle italic text-lg md:text-xl text-text-primary leading-relaxed" data-testid="streaming-note-text">
                « {typedNote}<span className="animate-pulse text-apollo-orange">▎</span>
              </p>
            </motion.div>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-6">
          <ResultHeader payload={result.ai_payload} typedNote={typedNote} done={phase === "done"} groupeLocale={groupeLocale} />
          {phase === "done" && (
            <>
              <RecommendationsList payload={result.ai_payload} groupeLocale={groupeLocale} />
              <AntiRecosList payload={result.ai_payload} groupeLocale={groupeLocale} />
              <Plan90Card payload={result.ai_payload} />
              <div className="flex flex-wrap gap-3 justify-center pt-4 pb-12">
                <Button onClick={() => navigate(`/groupe/${groupeId}`)} variant="outline" data-testid="btn-back-group">
                  <ArrowLeft className="w-4 h-4 mr-2" /> {t("nav.back")}
                </Button>
                <Button onClick={() => { setPhase("ready"); setResult(null); }} className="bg-apollo-orange hover:bg-apollo-orange-dark text-white" data-testid="btn-relancer">
                  <Sparkles className="w-4 h-4 mr-2" /> {t("diag.regenerate")}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ReadyView({ onStart }) {
  const { t } = useTranslation();
  return (
    <div className="max-w-2xl mx-auto text-center py-12" data-testid="diagnostic-ready">
      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-apollo-orange-light/60 backdrop-blur mb-6 relative">
        <div className="w-16 h-16 rounded-full bg-apollo-orange flex items-center justify-center shadow-2xl shadow-apollo-orange/30">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
      </div>
      <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mb-4">
        {t("diag.ready_title", "L'oracle est prêt.")}
      </h1>
      <p className="font-oracle italic text-2xl text-apollo-orange-dark mb-8 leading-relaxed">
        « {t("diag.ready_subtitle", "Ouvrez les portes du temple. DELPHI lira votre groupe et révélera ce qu'il doit vivre.")} »
      </p>
      <Button onClick={onStart} className="h-14 px-8 bg-apollo-orange hover:bg-apollo-orange-dark text-white text-lg font-semibold" data-testid="btn-start-diagnostic">
        <Sparkles className="w-5 h-5 mr-2" /> {t("diag.start", "Lancer le diagnostic")}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      <p className="text-xs text-text-secondary mt-6">
        {t("diag.ready_meta", "~ 30 secondes · Analyse BPM, KPI, historique · Recommandations + Anti-recos")}
      </p>
    </div>
  );
}

function ResultHeader({ payload, typedNote, done, groupeLocale }) {
  const { t } = useTranslation();
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-border rounded-2xl p-6 md:p-8"
      data-testid="result-header"
    >
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-shrink-0">
          <HealthScore value={payload.sante_globale} testId="result-health-score" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">{t("diag.oracle_note")}</div>
          {payload.degraded && (
            <span className="inline-block mb-2 text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300" data-testid="diag-degraded-badge">
              {t("diag.degraded_badge")}
            </span>
          )}
          <h2 className="font-heading font-black text-xl md:text-2xl tracking-tight mb-3">{payload.etat_synthese}</h2>
          <p className={`font-oracle italic text-lg md:text-xl text-text-primary leading-relaxed ${!done ? "typewriter" : ""}`} data-testid="oracle-note">
            « {typedNote}{done ? " »" : ""}
          </p>

          {done && payload.symptomes_detectes?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {payload.symptomes_detectes.map((s, i) => (
                <motion.span
                  key={s.code || `sym-${i}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.08 }}
                  className={`text-xs font-medium px-3 py-1 rounded-full ${
                    s.severite === "forte" ? "bg-danger/10 text-danger" :
                    s.severite === "moyenne" ? "bg-warning/10 text-warning" :
                    "bg-bg-soft text-text-secondary"
                  }`}
                  data-testid={`symptom-${s.code}`}
                >
                  {signalLabel(s, groupeLocale)}
                </motion.span>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}

function RecommendationsList({ payload, groupeLocale }) {
  const { t } = useTranslation();
  const [ateliers, setAteliers] = useState({});
  useEffect(() => {
    const ids = (payload.recommandations || []).map((r) => r.atelier_id);
    Promise.all(ids.map((id) => api.get(`/ateliers/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => {
        const map = {};
        arr.forEach((a) => { if (a) map[a.id] = a; });
        setAteliers(map);
      });
  }, [payload]);

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} data-testid="recommendations-section">
      <h2 className="font-heading font-black text-2xl tracking-tighter mb-1">{t("diag.prescribed")}</h2>
      <p className="font-oracle italic text-text-secondary mb-4">{t("diag.prescribed_subtitle")}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(payload.recommandations || []).map((r, i) => (
          <motion.div
            key={r.atelier_id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.12 }}
            className="bg-white border-2 border-apollo-orange/20 rounded-xl p-5 relative"
            data-testid={`reco-${r.atelier_id}`}
          >
            <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-apollo-orange text-white flex items-center justify-center font-heading font-black text-base shadow-md">
              {r.priorite}
            </div>
            {ateliers[r.atelier_id] && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-bg-soft border border-border">{r.atelier_id}</span>
                  <ConfidenceBadge confidence={r.confidence} compact />
                </div>
                <h3 className="font-heading font-bold text-base text-text-primary leading-snug">{pickTitle(ateliers[r.atelier_id], groupeLocale)}</h3>
              </div>
            )}
            <p className="text-sm text-text-secondary leading-relaxed">{r.raisonnement}</p>
            <Link to={`/atelier/${r.atelier_id}`} className="inline-flex items-center gap-1 text-sm font-medium text-apollo-orange-dark mt-3 hover:underline" data-testid={`btn-voir-reco-${r.atelier_id}`}>
              {t("nav.view_workshop")} <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}

function AntiRecosList({ payload, groupeLocale }) {
  const { t } = useTranslation();
  const [ateliers, setAteliers] = useState({});
  useEffect(() => {
    const ids = (payload.anti_recommandations || []).map((r) => r.atelier_id);
    Promise.all(ids.map((id) => api.get(`/ateliers/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => {
        const map = {};
        arr.forEach((a) => { if (a) map[a.id] = a; });
        setAteliers(map);
      });
  }, [payload]);

  if (!payload.anti_recommandations?.length) return null;

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} data-testid="anti-recos-section">
      <h2 className="font-heading font-black text-2xl tracking-tighter mb-1">{t("diag.anti")}</h2>
      <p className="font-oracle italic text-text-secondary mb-4">{t("diag.anti_subtitle")}</p>
      <div className="space-y-3">
        {payload.anti_recommandations.map((r, i) => (
          <motion.div key={r.atelier_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + i * 0.1 }}>
            <AntiRecoCard atelier={ateliers[r.atelier_id]} raisonnement={r.raisonnement} titleLocale={groupeLocale} />
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}

function Plan90Card({ payload }) {
  const { t } = useTranslation();
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="bg-gradient-to-br from-apollo-orange-light/40 via-white to-bg-soft border border-apollo-orange/20 rounded-2xl p-6 md:p-8"
      data-testid="plan-90-section"
    >
      <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">{t("diag.plan_90")}</div>
      <h2 className="font-heading font-black text-2xl tracking-tighter mb-3">{t("diag.plan_progression")}</h2>
      <p className="font-oracle italic text-lg text-text-primary leading-relaxed">« {payload.plan_90_jours} »</p>
    </motion.section>
  );
}
